import React, { useEffect, useState } from 'react';
import {
  Bitcoin,
  FileDigit,
  FileDown,
  Layers,
  TerminalSquare,
  Play,
  CheckCircle2,
  ArrowRight,
  Calculator,
  Copy,
  Hash,
  ArrowLeftRight
} from 'lucide-react';

import {
  Box,
  Container,
  Stack,
  Title,
  Text,
  Paper,
  SimpleGrid,
  Button,
  Group,
  TextInput,
  ScrollArea,
  Table,
  Tooltip,
  Code,
  List,
  ThemeIcon,
  Center,
  Loader
} from '@mantine/core';
import { useTranslation } from 'react-i18next';

import VerifyTable from "./VerifyTable.jsx";
import useVerification from './verificationProvider.js';
import ErrorScreen from './ErrorScreen.jsx';

import useUser from './userProvider';

async function sha256HexFromHex(hexInput) {
  if (!/^[0-9a-fA-F]*$/.test(hexInput) || hexInput.length % 2 !== 0) {
    throw new Error('Input must be an even-length hex string');
  }

  const bytes = new Uint8Array(
    hexInput.match(/.{2}/g).map((b) => parseInt(b, 16))
  );

  const hashBuffer = await crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(hashBuffer)]
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export default function OtsVerificationViz({params}) {
  const { t } = useTranslation();
  const [activeStepId, setActiveStepId] = useState(null);
  const [copiedGlobal, setCopiedGlobal] = useState(null);

  // Calculator State
  const [calcInput, setCalcInput] = useState('');
  const [calcOutput, setCalcOutput] = useState('');
  const [calcExpected, setCalcExpected] = useState('');

  const {user, repo, date} = params;
  const {userInfo} = useUser();

  useEffect(() => {
    document.title = `${user}/${repo} — ${date} — Timestamp GIT`;
  }, [user, repo, date]);

  const {data: steps, error, mutate} = useVerification(user, repo, date);

  // Handle clicking "Verify" on a specific row
  const handleVerifyRow = async (step) => {
    const concat = `${step.prepend||""}${step.current||""}${step?.append||""}`;
    setCalcInput(concat);
    setCalcExpected(step.nextExpected);
    setActiveStepId(step.id);
    navigator.clipboard.writeText(concat);

    if (step.stepNum === 42) {
      setCalcOutput(t('Broadcast to Bitcoin Network'));
    } else {
      setCalcOutput(await sha256HexFromHex(concat));
    }
  };

  // Handle manual "Hash It" button click
  const handleManualCalc = async () => {
    if (!calcInput) return;
    setCalcOutput(await sha256HexFromHex(calcInput));
  };

  // Handle byte inversion (Little-Endian <-> Big-Endian)
  const invertHexBytes = (str) => {
    const cleanStr = str.trim();
    // Only invert if it's a valid, even-length hex string
    if (!/^[0-9a-fA-F]+$/.test(cleanStr) || cleanStr.length % 2 !== 0) return str;

    let inverted = '';
    for (let i = cleanStr.length - 2; i >= 0; i -= 2) {
      inverted += cleanStr.substring(i, i + 2);
    }
    return inverted;
  };

  const handleInvertInput = () => setCalcInput(invertHexBytes(calcInput));
  const handleInvertOutput = () => setCalcOutput(invertHexBytes(calcOutput));

  // General Copy Function
  const copyToClipboard = (text, id) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedGlobal(id);
    setTimeout(() => setCopiedGlobal(null), 2000);
  };

  if ((userInfo == null) && (error?.status === 403)) {
    const url = new URL("/api/login", document.location);
    const params = new URLSearchParams({cont: document.location});
    url.search = params.toString();
    document.location = url.toString();
    return;
    }

  if (error) {
    return (
      <ErrorScreen
        status={error.status}
        title={t('Verification data unavailable')}
        message={t('We could not load the verification steps for this repository and date.')}
        onRetry={() => mutate()}
      />
    );
  }

  if (!steps) {
    return (
      <Box mih="40vh">
        <Container fluid px="md">
          <Stack align="center" justify="center" py={40}>
            <Loader />
            <Text c="dimmed">{t('Loading verification data...')}</Text>
          </Stack>
        </Container>
      </Box>
    );
  }

  return (
    <Box mih="100vh" py={40}>
      <Container fluid px="md">
        <Stack gap="lg">
          <Stack gap={4}>
            <Title order={1}>{t('Calculatable Verification')}</Title>
            <Text c="dimmed" size="sm" maw={720}>
              {t('The full mathematical path mapping your commit to Bitcoin. Concatenate strings, hash via SHA-256, and pass the result down.')}
            </Text>
          </Stack>

          <Paper withBorder radius="md" p="md">
            <Group gap="xl" wrap="wrap">
              <Group gap="xs">
                <Text size="sm" fw={600} c="dimmed">{t('Repository')}:</Text>
                <Text size="sm" ff="monospace" fw={500}>{user}/{repo}</Text>
              </Group>
              <Group gap="xs">
                <Text size="sm" fw={600} c="dimmed">{t('Date')}:</Text>
                <Text size="sm" ff="monospace" fw={500}>{date}</Text>
              </Group>
            </Group>
          </Paper>

          <Paper withBorder radius="md" p="md">
            <Stack gap="sm">
              <Group align="end" wrap="wrap">
                <TextInput
                  flex={1}
                  label={
                    <Group gap={6}>
                      <Calculator size={14} />
                      <Text size="sm">{t('Input')}</Text>
                    </Group>
                  }
                  value={calcInput}
                  onChange={(e) => setCalcInput(e.target.value)}
                  placeholder={t('Paste concatenated hex string here (Left + Middle + Right)...')}
                />
                <Button variant="light" leftSection={<ArrowLeftRight size={14} />} onClick={handleInvertInput}>
                  {t('Invert')}
                </Button>
                <Button leftSection={<Hash size={14} />} onClick={handleManualCalc}>
                  {t('Calculate SHA-256')}
                </Button>
              </Group>

              <Paper withBorder radius="sm" p="sm">
                <Group justify="space-between" align="start" wrap="wrap">
                  <Stack gap={2} flex={1}>
                    <Group gap={6}>
                      <ArrowRight size={14} />
                      <Text size="sm" fw={600}>{t('Output')}</Text>
                    </Group>
                    <Code block>{calcOutput || t('Hash result will appear here...')}</Code>
                  </Stack>
                  <Group>
                    <Button variant="light" leftSection={<ArrowLeftRight size={14} />} onClick={handleInvertOutput}>
                      {t('Invert')}
                    </Button>
                    <Button
                      variant="light"
                      leftSection={copiedGlobal === 'calc_out' ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                      onClick={() => copyToClipboard(calcOutput, 'calc_out')}
                    >
                      {t('Copy Output')}
                    </Button>
                  </Group>
                </Group>
              </Paper>

              {calcOutput === calcExpected && calcOutput !== '' && (
                <Group>
                  <ThemeIcon color="teal" variant="light" size="sm">
                    <CheckCircle2 size={12} />
                  </ThemeIcon>
                  <Text size="sm" c="teal">{t('Matched')}</Text>
                </Group>
              )}
            </Stack>
          </Paper>

          <VerifyTable activeStepId={activeStepId} steps={steps} onVerifyRow={handleVerifyRow} user={user} repo={repo} />
          
          <Button
            component="a"
            href={`/api/report/${user}/${repo}/${date}`}
            variant="light"
            leftSection={<FileDown size={14} />}
            fullWidth
          >{t('Download PDF Report')}</Button>

          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Paper withBorder radius="md" p="md">
              <Stack gap="sm">
                <Group gap={8}>
                  <TerminalSquare size={16} />
                  <Text fw={600}>{t('Trust Math, Not Servers')}</Text>
                </Group>
                <List spacing="sm">
                  <List.Item
                    icon={
                      <ThemeIcon size="sm" variant="light" color="blue">
                        <FileDigit size={12} />
                      </ThemeIcon>
                    }
                  >
                    <Text size="sm">
                      <Text span fw={600}>{t('Start:')} </Text>
                      {t('Row 1 "Current Result" is your local Git commit hash.')}
                    </Text>
                  </List.Item>
                  <List.Item
                    icon={
                      <ThemeIcon size="sm" variant="light" color="violet">
                        <Layers size={12} />
                      </ThemeIcon>
                    }
                  >
                    <Text size="sm">
                      <Text span fw={600}>{t('Iterate:')} </Text>
                      {t('Click "Verify" to load the row into the calculator above. Hash it to match the next row.')}
                    </Text>
                  </List.Item>
                </List>
              </Stack>
            </Paper>

            <Paper withBorder radius="md" p="md">
              <Center>
                <Stack align="center" gap={6}>
                  <Bitcoin size={32} />
                  <Title order={3}>{t('Zero Knowledge Architecture')}</Title>
                  <Text c="dimmed" size="sm" ta="center" maw={400}>
                    {t('Because you can verify all 42 levels locally, you never need to expose your source code to us.')}
                  </Text>
                </Stack>
              </Center>
            </Paper>
          </SimpleGrid>
        </Stack>
      </Container>
    </Box>
  );
}